import { CircleAlert, CircleCheck, Info, TriangleAlert } from 'lucide-react';
import type { CSSProperties, ReactNode } from 'react';
import { cx } from './cx';
import styles from './Feedback.module.css';

export type Tone = 'neutral' | 'primary' | 'success' | 'warning' | 'danger' | 'info' | 'accent';

/** Status label: text + icon, never color alone. */
export function Badge({
  tone = 'neutral',
  icon,
  children,
  className,
}: {
  tone?: Tone;
  icon?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span className={cx(styles.badge, styles[tone], className)}>
      {icon}
      <span>{children}</span>
    </span>
  );
}

const CALLOUT_ICONS: Record<'info' | 'success' | 'warning' | 'danger', ReactNode> = {
  info: <Info size={18} aria-hidden="true" />,
  success: <CircleCheck size={18} aria-hidden="true" />,
  warning: <TriangleAlert size={18} aria-hidden="true" />,
  danger: <CircleAlert size={18} aria-hidden="true" />,
};

/** Inline notice. Use role="status" for results that appear after an action. */
export function Callout({
  tone = 'info',
  title,
  children,
  role,
  className,
  action,
}: {
  tone?: 'info' | 'success' | 'warning' | 'danger';
  title?: ReactNode;
  children?: ReactNode;
  role?: 'status' | 'alert' | 'note';
  className?: string;
  action?: ReactNode;
}) {
  return (
    <div className={cx(styles.callout, styles[tone], className)} role={role}>
      {CALLOUT_ICONS[tone]}
      <div className={styles.calloutBody}>
        {title ? <p className={styles.calloutTitle}>{title}</p> : null}
        {children ? <div>{children}</div> : null}
        {action ? <div>{action}</div> : null}
      </div>
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  children,
  action,
}: {
  icon: ReactNode;
  title: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className={styles.empty}>
      <span className={styles.emptyIcon} aria-hidden="true">
        {icon}
      </span>
      <h2 className={styles.emptyTitle}>{title}</h2>
      {children ? <p className={styles.emptyText}>{children}</p> : null}
      {action}
    </div>
  );
}

export function Skeleton({
  width = '100%',
  height = '1rem',
  className,
}: {
  width?: CSSProperties['inlineSize'];
  height?: CSSProperties['blockSize'];
  className?: string;
}) {
  return (
    <span
      className={cx(styles.skeleton, className)}
      style={{ inlineSize: width, blockSize: height }}
      aria-hidden="true"
    />
  );
}
